import { BoxRenderable, RootRenderable } from "@opentui/core";
import { createSignal } from "solid-js/dist/solid.js";
import { RendererContext } from "./elements/index.js";
import { _render as renderInternal, createComponent } from "./reconciler.js";
let solidScrollbackRootCounter = 0;
const MAX_AUTO_HEIGHT_PASSES = 4;
function normalizeSnapshotDimension(value, axis) {
  if (value === undefined) {
    return undefined;
  }
  if (!Number.isFinite(value)) {
    throw new Error(`createScrollbackWriter requires a finite ${axis}`);
  }
  return Math.max(1, Math.trunc(value));
}
function createSnapshotRendererValue(renderContext, root, width, height, firstLineOffset) {
  const [snapshotWidth] = createSignal(width);
  const [snapshotHeight, setSnapshotHeight] = createSignal(height);
  const renderer = Object.create(renderContext);
  let offset = firstLineOffset;
  Object.defineProperties(renderer, {
    root: {
      value: root,
      enumerable: true,
      configurable: true
    },
    width: {
      get: snapshotWidth,
      enumerable: true,
      configurable: true
    },
    height: {
      get: snapshotHeight,
      enumerable: true,
      configurable: true
    },
    claimFirstLineOffset: {
      value: () => {
        const out = offset;
        offset = 0;
        return out;
      },
      enumerable: true,
      configurable: true
    }
  });
  return {
    renderer,
    getHeight: snapshotHeight,
    setHeight(nextHeight) {
      setSnapshotHeight(nextHeight);
      renderer.emit("resize", snapshotWidth(), nextHeight);
    }
  };
}
function runLifecyclePasses(renderContext) {
  for (const renderable of renderContext.getLifecyclePasses()) {
    renderable.onLifecyclePass?.call(renderable);
  }
}
function clearLifecyclePasses(renderContext) {
  for (const renderable of [...renderContext.getLifecyclePasses()]) {
    renderContext.unregisterLifecyclePass(renderable);
  }
}
function measureSnapshotHeight(renderContext, root) {
  const measureRoot = new RootRenderable(renderContext);
  try {
    measureRoot.add(root);
    runLifecyclePasses(renderContext);
    measureRoot.calculateLayout();
    return Math.max(1, Math.trunc(root.getLayoutNode().getComputedLayout().height));
  } finally {
    if (root.parent === measureRoot) {
      measureRoot.remove(root.id);
    }
    measureRoot.destroyRecursively();
  }
}
function resolveSnapshotHeight(renderContext, root, snapshotRenderer) {
  for (let pass = 0; pass < MAX_AUTO_HEIGHT_PASSES; pass++) {
    const measuredHeight = measureSnapshotHeight(renderContext, root);
    if (measuredHeight === snapshotRenderer.getHeight()) {
      clearLifecyclePasses(renderContext);
      return measuredHeight;
    }
    snapshotRenderer.setHeight(measuredHeight);
  }

  // Give up on converging the synthetic height and let the final render rerun
  // lifecycle passes against the last consistent tree state.
  return measureSnapshotHeight(renderContext, root);
}
export function createScrollbackWriter(node, options = {}) {
  return ctx => {
    const width = normalizeSnapshotDimension(options.width, "width") ?? Math.max(1, Math.trunc(ctx.width));
    const height = normalizeSnapshotDimension(options.height, "height");
    const startOnNewLine = options.startOnNewLine ?? true;
    const firstLineWidth = !startOnNewLine && ctx.tailColumn > 0 && ctx.tailColumn < ctx.width ? Math.min(width, ctx.width - ctx.tailColumn) : width;
    const firstLineOffset = width - firstLineWidth;
    const root = new BoxRenderable(ctx.renderContext, {
      id: `solid-scrollback-root-${solidScrollbackRootCounter++}`,
      position: "absolute",
      left: 0,
      top: 0,
      width,
      height: height ?? "auto",
      border: false,
      backgroundColor: "transparent",
      shouldFill: false,
      flexDirection: "column"
    });
    const snapshotRenderer = createSnapshotRendererValue(ctx.renderContext, root, width, height ?? Math.max(1, ctx.renderContext.height), firstLineOffset);
    let dispose;
    let disposed = false;
    const teardown = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      dispose?.();
    };
    try {
      dispose = renderInternal(() => createComponent(RendererContext.Provider, {
        get value() {
          return snapshotRenderer.renderer;
        },
        get children() {
          return node(ctx);
        }
      }), root);
      return {
        root,
        width,
        height: height ?? resolveSnapshotHeight(ctx.renderContext, root, snapshotRenderer),
        rowColumns: options.rowColumns,
        startOnNewLine,
        trailingNewline: options.trailingNewline,
        teardown
      };
    } catch (error) {
      teardown();
      root.destroyRecursively();
      throw error;
    }
  };
}
export function writeSolidToScrollback(renderer, node, options = {}) {
  renderer.writeToScrollback(createScrollbackWriter(node, options));
}